/**
 * InboundDocumentDetail — full view of one inbound document (WL-A6): §10 badges,
 * extracted fields (documentDate / amounts / tax breakdown), artifact downloads
 * via `getInboundDocumentFile`, and (Premium) "Als Ausgabe buchen". NO delete.
 *
 * @module InboundDocumentDetail
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  IconArrowLeft,
  IconDownload,
  IconMail,
  IconDocument,
  IconExpense,
  IconSearch,
  IconSpinner,
} from '../../icons';
import type {
  InboundDocument,
  InboundTaxDecision,
  InboundTaxScheme,
} from '../../types/inboundTypes';
import {
  deriveInboundBadges,
  type InboundBadgeDescriptor,
} from '../../utils/inboundAdapters';
import { resolveEffectiveTaxScheme } from '../../utils/inboundTaxDecision';
import InboundTaxDecisionPanel from './InboundTaxDecisionPanel';
import {
  getInboundDocumentFile,
  inboundFileResultToUrl,
  type InboundArtifact,
} from '../../api/inboundApi';
import type { VatConfirmationStatus } from '../../api/vatConfirmationApi';
import logger from '../../utils/logger';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';
import { useAuth } from '../../context/AuthContext';
import { canUseKanzleiMessaging } from '../../config/kanzleiAccess';
import MessageContextButton from '../messaging/MessageContextButton';
import InboundBadgeList from './InboundBadgeList';
import InboundPruefprotokoll from './InboundPruefprotokoll';
import VerifiedOriginalSeal from './VerifiedOriginalSeal';
import VatConfirmationBadge from './VatConfirmationBadge';

interface InboundDocumentDetailProps {
  doc: InboundDocument;
  onBack: () => void;
  /** Whether the "Als Ausgabe buchen" action is available (Premium + flag + owner). */
  canBookExpense: boolean;
  onBookExpense: (doc: InboundDocument) => void;
  /**
   * WL-K11: whether the viewer is Loop-B beta (`isLoopBBeta(uid, sellerData)`,
   * computed by the page). Gates the "Verifiziert gegen Original" seal alongside
   * the document's own `verified.verifiedAgainstOriginal` flag. Fail-closed
   * default `false` — no seal for non-beta viewers or legacy call sites.
   */
  showVerifiedSeal?: boolean;
  /**
   * WL-K12: whether the viewer is Loop-B beta (`isLoopBBeta(uid, sellerData)`,
   * computed by the page, same source as `showVerifiedSeal`). Gates the
   * "USt-IdNr. prüfen"-Aktion (§18e-Bestätigung) alongside the seller's
   * `extracted.sellerVatId`. Fail-closed default `false`.
   */
  showVatCheck?: boolean;
  /**
   * Manuelle Steuerkontierung (architecture-eingang-steuerkontierung.md §5.4):
   * ob die Kontierungs-Aktion sichtbar ist (owner + Flag/Beta; die Seite
   * berechnet via `canUseInboundTaxDecision`). Fail-closed default `false` —
   * bestehende Aufrufstellen kompilieren unverändert.
   */
  canDecideTax?: boolean;
  /**
   * Nach erfolgreichem Kontierungs-Write: die Seite merged den neuen Summary-
   * Stand in ihren `docs`/`selected`-State (kein Full-Reload). `null` = aufgehoben.
   */
  onTaxDecided?: (docId: string, taxDecision: InboundTaxDecision | null) => void;
}

/** Decimal-euro → de-DE currency. */
function fmtAmount(value: number | null | undefined, currency: string | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '–';
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency && currency.length === 3 ? currency : 'EUR',
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency ?? 'EUR'}`;
  }
}

/** Fallback strings for the §13b/mixed/IG/unknown hint keys (§5.7 exact wording, Bug-4-Fix for 'unknown'). */
const DEFAULT_TAX_SCHEME_HINTS: Record<string, string> = {
  'pruefprotokoll.reverseCharge13bHint':
    'Steuerschuldnerschaft des Leistungsempfängers (§13b UStG) — die Umsatzsteuer ist vom Rechnungsempfänger zu berechnen und abzuführen. Bitte Kontierung/Steuerschlüssel prüfen.',
  'pruefprotokoll.intraCommunityHint':
    'Innergemeinschaftlicher Erwerb — Erwerbsbesteuerung durch den Empfänger (§1a UStG). Bitte Steuerschlüssel prüfen.',
  'pruefprotokoll.mixedHint':
    'Dieser Beleg enthält mehrere unterschiedliche Steuerkategorien. Bitte Kontierung/Steuerschlüssel prüfen.',
  'pruefprotokoll.taxSchemeUnknownHint':
    'Die Steuerkategorie dieses Belegs konnte nicht automatisch bestimmt werden. Bitte Kontierung/Steuerschlüssel manuell prüfen.',
};

const InboundDocumentDetail: React.FC<InboundDocumentDetailProps> = ({
  doc,
  onBack,
  canBookExpense,
  onBookExpense,
  showVerifiedSeal = false,
  showVatCheck = false,
  canDecideTax = false,
  onTaxDecided,
}) => {
  const { t } = useTranslation('posteingang');
  const [downloading, setDownloading] = useState<InboundArtifact | null>(null);
  const [vatChecking, setVatChecking] = useState(false);
  const [vatCheckStatus, setVatCheckStatus] = useState<VatConfirmationStatus | null>(null);

  // W1-D (architecture-cockpit-intelligence.md, Aktion 1c owner side): the
  // message-thread trigger reuses the SAME isLoopBBeta(uid, sellerData) source
  // as showVerifiedSeal/showVatCheck above — computed locally via useAuth()
  // rather than threaded in as a prop from InboxPage.tsx (kept out of this
  // wave's file set; identical fail-closed result either way, see changelog).
  // MessageContextButton itself gates a second time on
  // canUseMessaging(effectiveScopeUid) — double gate is intentional (§Beta-
  // Gating Cross-Gate-Abhängigkeit).
  const { currentUser, sellerData } = useAuth();
  const showMessaging = canUseKanzleiMessaging(currentUser?.uid, sellerData);

  const badges: InboundBadgeDescriptor[] = deriveInboundBadges(doc);
  const ex = doc.extracted;
  const currency = ex?.currency;

  // §13b/mixed/IG-Hinweis (IDV-4, architecture-inbound-datev.md §5.7 — exact
  // wording for reverse_charge/reverse_charge_mixed and intra_community; the
  // 'mixed' hint is a neutral, non-legal "please check" note not dictated by
  // §5.7). Deliberately NO computed tax amount is shown alongside this hint —
  // `taxScheme` is a classification, not a tax-liability calculation.
  // Manuelle Steuerkontierung (architecture-eingang-steuerkontierung.md §6.4):
  // die Hinweis-Auswahl läuft über das EFFEKTIVE Scheme (Auto-Wert ODER eine
  // aktive, nicht-stale manuelle Entscheidung) statt roh `doc.taxScheme` — der
  // §13b-Pflichthinweis bleibt so auch bei manuell gesetztem reverse_charge.
  const effectiveTax = resolveEffectiveTaxScheme(doc);
  const taxScheme: InboundTaxScheme = effectiveTax.scheme;
  // Bug-4-Fix (bug-analyse-2026-07-17.md): der Fall 'unknown' fehlte in dieser Ternary — das
  // Listen-Badge (deriveInboundBadges, s.o.) flaggt "Bitte prüfen" für exakt denselben Fall,
  // im Detail gab es dafür aber KEINEN prominenten Erklärkasten (nur die kleine Badge-Pille),
  // während das positive EN-16931-Prüfprotokoll optisch dominierte. Jetzt bekommt 'unknown'
  // denselben prominenten Kasten wie reverse_charge/intra_community/mixed.
  const taxSchemeHintKey: string | null =
    taxScheme === 'reverse_charge' || taxScheme === 'reverse_charge_mixed'
      ? 'pruefprotokoll.reverseCharge13bHint'
      : taxScheme === 'intra_community'
        ? 'pruefprotokoll.intraCommunityHint'
        : taxScheme === 'mixed'
          ? 'pruefprotokoll.mixedHint'
          : taxScheme === 'unknown'
            ? 'pruefprotokoll.taxSchemeUnknownHint'
            : null;

  const handleDownload = useCallback(
    async (artifact: InboundArtifact) => {
      setDownloading(artifact);
      try {
        const result = await getInboundDocumentFile(doc.id, artifact);
        const { url, fileName, revoke } = inboundFileResultToUrl(result);
        // Trigger a download (inline blob) or open the signed URL.
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.rel = 'noopener';
        if (result.mode === 'signedUrl') a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoke the blob URL after the click has been processed.
        window.setTimeout(revoke, 10_000);
      } catch (err: any) {
        logger.error(err, 'inbound document download failed');
        toast.error(mapErrorToUserMessage(err, t, { fallbackKey: 'download.error' }));
      } finally {
        setDownloading(null);
      }
    },
    [doc.id, t],
  );

  /**
   * WL-K12: fordert eine §18e-Bestätigung für die USt-IdNr. des Ausstellers
   * an (Kontext `seller` — der Aussteller dieses Eingangsbelegs). Alle harten
   * Checks (Beta-Gate, fail-closed BZSt-Call) laufen serverseitig; hier wird
   * nur das Ergebnis für die Badge-Anzeige übernommen.
   *
   * Der `vatConfirmationApi`-Import ist bewusst LAZY (dynamisches `import()`
   * statt Modul-Top-Level) — dieser Aktion ist optional/beta und wird nur bei
   * Klick geladen; das vermeidet außerdem, dass das reale `firebaseConfig`
   * (Firebase-App-Init) in Tests initialisiert wird, die diese Aktion nie
   * auslösen und dieses Modul nicht mocken (s. changelog-loop-b-inbound-vat.md).
   */
  const handleVatCheck = useCallback(async () => {
    if (!ex?.sellerVatId) return;
    setVatChecking(true);
    try {
      const { requestVatIdConfirmation } = await import('../../api/vatConfirmationApi');
      const result = await requestVatIdConfirmation({
        vatId: ex.sellerVatId,
        context: 'seller',
        companyName: ex.sellerName || null,
        sourceRef: `inbound_documents/${doc.id}`,
      });
      setVatCheckStatus(result.status);
    } catch (err: any) {
      logger.error(err, 'vat id confirmation request failed');
      toast.error(mapErrorToUserMessage(err, t, { fallbackKey: 'vatCheck.error' }));
    } finally {
      setVatChecking(false);
    }
  }, [ex?.sellerVatId, ex?.sellerName, doc.id, t]);

  const hasEml = doc.source === 'email' && !!doc.emlPath;
  const hasReport = !!doc.validation?.reportPath;

  return (
    <div className="bg-surface-raised border border-hairline rounded-2xl p-4 md:p-6">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-muted hover:text-primary mb-4"
      >
        <IconArrowLeft size={12} />
        {t('detail.back', 'Zurück zum Posteingang')}
      </button>

      <div className="mb-4">
        <InboundBadgeList badges={badges} />
      </div>

      {/* WL-K11 Verified-Invoice seal — the <2-min pilot wow-moment (§5.5).
          Renders only when the viewer is Loop-B beta AND the document really
          matched the issuer's original (fail-closed; component self-gates). */}
      <VerifiedOriginalSeal verified={doc.verified} enabled={showVerifiedSeal} />

      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="text-xl font-bold text-primary">
          {ex?.sellerName || doc.mail?.from || t('unknownSender', 'Unbekannter Absender')}
        </h1>
        {/* Aktion 1c (Welle 1 Cockpit-Intelligenz), owner side: opens the same
            receipt-scoped thread the StB-Cockpit posts pre-filled questions
            into (refCollection='inbound_documents'). */}
        {showMessaging && (
          <MessageContextButton
            context={{ type: 'receipt', refId: doc.id }}
            refCollection="inbound_documents"
          />
        )}
      </div>
      {ex?.invoiceNumber && (
        <p className="text-muted text-sm mb-4">{ex.invoiceNumber}</p>
      )}

      {/* §13b/mixed/IG-Hinweis (IDV-4, §5.7) — classification note, no computed
          tax amount. Only shown once the document has an evaluated taxScheme
          worth flagging; standard/exempt/etc. render nothing here. */}
      {taxSchemeHintKey && (
        <div className="mb-4 bg-[rgb(var(--surface-base-rgb)/0.4)] border border-hairline rounded-lg p-3 text-sm text-secondary">
          {t(taxSchemeHintKey, DEFAULT_TAX_SCHEME_HINTS[taxSchemeHintKey])}
        </div>
      )}

      {/* Stale-Hinweis (§5.2/§6): eine manuelle Entscheidung, die sich auf einen
          früheren Auswertungsstand bezog, wird nicht mehr angewendet (J-1(d)). */}
      {effectiveTax.stale && (
        <div className="mb-4 bg-warn/10 border border-warn/30 rounded-lg p-3 text-sm text-ink">
          {t(
            'taxDecision.stale',
            'Diese manuelle Einordnung bezog sich auf einen früheren Auswertungsstand des Belegs und wird nicht mehr angewendet. Bitte prüfen Sie die Steuerkategorie erneut.',
          )}
        </div>
      )}

      {/* Manuelle Kontierung (owner-only, gated): Trigger/Formular/Bestätigungs-
          Ansicht — das Panel leitet seinen Zustand aus resolveEffectiveTaxScheme
          ab. Nur gerendert, wenn die Seite canDecideTax setzt (§5.4/§6). */}
      {canDecideTax && (
        <div className="mb-4">
          <InboundTaxDecisionPanel
            doc={doc}
            onDecided={(taxDecision) => onTaxDecided?.(doc.id, taxDecision)}
          />
        </div>
      )}

      {/* Prominent "open original" action (Jurist-Auflage #7a, polish pass
          posteingang-polish): the original document is the legally relevant
          artifact — this must not compete visually with the secondary
          eml/report downloads below, so it gets its own emphasized row near
          the top, above the extracted-fields preview. Reuses the existing
          getInboundDocumentFile('original') flow (handleDownload below). */}
      <div className="mb-5">
        <button
          onClick={() => handleDownload('original')}
          disabled={downloading !== null}
          className="inline-flex items-center gap-2 text-base bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white px-5 py-3 rounded-xl font-semibold shadow-lg shadow-indigo-900/30 transition-colors"
        >
          <IconDownload />
          {downloading === 'original'
            ? t('detail.download.loading', 'Wird geladen …')
            : t('detail.download.original', 'Original herunterladen')}
        </button>
      </div>

      {/* Extracted fields */}
      {ex ? (
        <>
          <p className="text-xs text-faint mb-3">
            {t(
              'detail.extractHint',
              'Automatisch ausgelesene Werte — unverbindlich, keine Buchungsgrundlage. Maßgeblich ist das Originaldokument.',
            )}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-5">
          <Field label={t('detail.fields.documentDate', 'Rechnungsdatum')} value={ex.documentDate || '–'} />
          <Field label={t('detail.fields.dueDate', 'Fällig am')} value={ex.dueDate || '–'} />
          <Field label={t('detail.fields.leistungsdatum', 'Leistungsdatum')} value={ex.leistungsdatum || '–'} />
          <Field label={t('detail.fields.netTotal', 'Netto')} value={fmtAmount(ex.netTotal, currency)} />
          <Field label={t('detail.fields.taxTotal', 'Steuer')} value={fmtAmount(ex.taxTotal, currency)} />
          <Field label={t('detail.fields.grossTotal', 'Brutto')} value={fmtAmount(ex.grossTotal, currency)} />
          {ex.sellerVatId && (
            <Field label={t('detail.fields.sellerVatId', 'USt-IdNr. Aussteller')} value={ex.sellerVatId} />
          )}
          {ex.buyerVatId && (
            <Field label={t('detail.fields.buyerVatId', 'USt-IdNr. Empfänger')} value={ex.buyerVatId} />
          )}
          </div>

          {/* WL-K12: §18e-Bestätigung der Aussteller-USt-IdNr. — nur Loop-B-Beta,
              nur wenn eine sellerVatId ausgelesen wurde. Rein additiv, on-demand
              (kein automatischer Trigger, s. changelog-loop-b-vat-check.md
              "offene Frage"). */}
          {showVatCheck && ex.sellerVatId && (
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleVatCheck}
                disabled={vatChecking}
                className="inline-flex items-center gap-2 text-xs bg-surface-inset hover:bg-surface-hover disabled:opacity-60 text-primary px-3 py-1.5 rounded-lg font-medium transition-colors"
              >
                {vatChecking ? <IconSpinner className="animate-spin" /> : <IconSearch size={12} />}
                {vatChecking
                  ? t('vatCheck.checking', 'Wird geprüft …')
                  : t('vatCheck.action', 'USt-IdNr. prüfen')}
              </button>
              <VatConfirmationBadge status={vatCheckStatus} />
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-faint mb-5">
          {t('detail.noExtract', 'Für diesen Beleg liegen keine strukturierten Felder vor.')}
        </p>
      )}

      {/* Tax breakdown */}
      {ex && ex.taxBreakdown.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-secondary mb-2">
            {t('detail.taxBreakdown', 'Steueraufschlüsselung')}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-faint">
                <tr>
                  <th className="py-1 pr-4">{t('detail.tax.rate', 'Satz')}</th>
                  <th className="py-1 pr-4">{t('detail.tax.base', 'Basis')}</th>
                  <th className="py-1 pr-4">{t('detail.tax.amount', 'Steuer')}</th>
                  <th className="py-1">{t('detail.tax.category', 'Kategorie')}</th>
                </tr>
              </thead>
              <tbody className="text-secondary">
                {ex.taxBreakdown.map((line, i) => (
                  <tr key={i} className="border-t border-hairline">
                    <td className="py-1 pr-4 tabular-nums">{line.taxRate} %</td>
                    <td className="py-1 pr-4 tabular-nums">{fmtAmount(line.taxBasisAmount, currency)}</td>
                    <td className="py-1 pr-4 tabular-nums">{fmtAmount(line.taxAmount, currency)}</td>
                    <td className="py-1">{line.categoryCode || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EN-16931 formal-check panel (IDV-4, §5.7) — neutral, never Auto-Reject. */}
      <InboundPruefprotokoll
        findings={doc.validation?.findings ?? []}
        grossTotal={ex?.grossTotal}
      />

      {/* Secondary actions — the "original" download moved above (prominent,
          Auflage #7a); this row keeps only the supplementary artifacts. */}
      <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
        {hasEml && (
          <button
            onClick={() => handleDownload('eml')}
            disabled={downloading !== null}
            className="inline-flex items-center gap-2 text-sm bg-surface-inset hover:bg-surface-hover disabled:opacity-60 text-primary px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <IconMail size={12} />
            {t('detail.download.eml', 'E-Mail herunterladen')}
          </button>
        )}

        {hasReport && (
          <button
            onClick={() => handleDownload('validationReport')}
            disabled={downloading !== null}
            className="inline-flex items-center gap-2 text-sm bg-surface-inset hover:bg-surface-hover disabled:opacity-60 text-primary px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <IconDocument size={12} />
            {t('detail.download.report', 'Prüfbericht')}
          </button>
        )}

        {canBookExpense && (
          <button
            onClick={() => onBookExpense(doc)}
            className="inline-flex items-center gap-2 text-sm bg-green-700 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <IconExpense size={12} />
            {t('detail.bookExpense', 'Als Ausgabe buchen')}
          </button>
        )}
      </div>

      {/* F1-B (jurist GA-Haftung, Textbaustein F1-B): Verantwortungs-Hinweis direkt am
          „Als Ausgabe buchen"-Button — die Übernahme beruht auf automatisch ausgelesenen
          Werten, die vor dem Buchen gegen das Original zu prüfen sind. */}
      {canBookExpense && (
        <p
          className="text-xs text-secondary mt-2"
          data-testid="inbound-booking-responsibility"
        >
          {t('detail.bookingResponsibility')}
        </p>
      )}
    </div>
  );
};

/** A single labelled read-only field. */
const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-faint">{label}</p>
    <p className="text-secondary">{value}</p>
  </div>
);

export default InboundDocumentDetail;
